import React, { useCallback } from 'react';

import { useIsPreviewPanelOpen } from '@atlassian/preview-panels-api/use-is-preview-panel-open';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { publishSeededRovoPrompt } from '../store/seededRovoPromptBridge.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

import { PANEL_ID_BASE, PANEL_PRODUCT, PANEL_TYPE } from './previewPanelConstants.ts';
import { TeamworkGraphPreviewPanel } from './TeamworkGraphPreviewPanel.tsx';

const getPanelId = (contentId?: string): string =>
	contentId ? `${PANEL_ID_BASE}_${contentId}` : PANEL_ID_BASE;

export const useTeamworkGraphPreviewPanel = (
	contentId?: string,
	contentType?: BylineContentType,
): {
	isTeamworkGraphPreviewPanelOpen: boolean;
	openTeamworkGraphPreviewPanel: () => void;
	openTeamworkGraphPreviewPanelWithRovo: (prompt: string) => void;
	closeTeamworkGraphPreviewPanel: () => void;
} => {
	const { open, close } = usePreviewPanelsActions();
	const panelId = getPanelId(contentId);
	// Asked for by id, not bare: `useIsPreviewPanelOpen()` reports any open preview panel,
	// so a page preview panel would read as this one.
	const isTeamworkGraphPreviewPanelOpen = useIsPreviewPanelOpen(panelId);

	const openTeamworkGraphPreviewPanel = useCallback(() => {
		open({
			panel: {
				id: panelId,
				type: PANEL_TYPE,
				product: PANEL_PRODUCT,
				content: () => (
					<TeamworkGraphPreviewPanel contentId={contentId} contentType={contentType} />
				),
			},
		});
	}, [open, panelId, contentId, contentType]);

	/**
	 * Opens the panel straight into a Rovo chat seeded with `prompt`, for callers with no Rovo of
	 * their own — the byline popup's share-context modal.
	 *
	 * Published before `open()`, so an already-open panel (where `open()` is a no-op) still sees
	 * the prompt. See `seededRovoPromptBridge`.
	 */
	const openTeamworkGraphPreviewPanelWithRovo = useCallback(
		(prompt: string) => {
			publishSeededRovoPrompt({ contentId, prompt });
			openTeamworkGraphPreviewPanel();
		},
		[contentId, openTeamworkGraphPreviewPanel],
	);

	const closeTeamworkGraphPreviewPanel = useCallback(() => close(), [close]);

	return {
		isTeamworkGraphPreviewPanelOpen,
		openTeamworkGraphPreviewPanel,
		openTeamworkGraphPreviewPanelWithRovo,
		closeTeamworkGraphPreviewPanel,
	};
};
